import type { SessionDto } from '@project-os/contracts';
import type { LoginInput, SetPasswordInput } from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from './client';

export const sessionQueryKey = ['session'] as const;

/**
 * Текущая сессия.
 *
 * Отсутствие входа — не ошибка, а состояние: 401 приводится к `null`, чтобы
 * маршрутизация могла просто отправить пользователя на страницу входа.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest<SessionDto>('/auth/session', { signal });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) =>
      apiRequest<SessionDto>('/auth/login', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionQueryKey, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      // Кэш очищается целиком: в нём лежат данные проектов прежнего пользователя.
      queryClient.clear();
      queryClient.setQueryData(sessionQueryKey, null);
    },
  });
}

/** Проверка одноразовой ссылки установки пароля. */
export function useInvitation(token: string) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: ({ signal }) =>
      apiRequest<{ name: string; email: string }>(
        `/auth/invitation?token=${encodeURIComponent(token)}`,
        { signal },
      ),
    enabled: token.length > 0,
    retry: false,
  });
}

export function useSetPassword() {
  return useMutation({
    mutationFn: (input: SetPasswordInput) =>
      apiRequest<void>('/auth/set-password', { method: 'POST', body: input }),
  });
}
